import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Battery, Check, Clock, Star } from 'lucide-react';
import { energyPlanning } from '../../data/toolsData';

const EnergyTool = () => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);

  const toggleTask = (levelId, taskIndex) => {
    const taskId = `${levelId}-${taskIndex}`;
    setCompletedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(t => t !== taskId)
        : [...prev, taskId]
    );
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#D4C4B0]/30 flex items-center justify-center mx-auto mb-6">
            <Battery size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {energyPlanning.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {energyPlanning.subtitle}
          </p>
        </div>

        {/* Energy Level Selection */}
        <div className="mb-8">
          <h2 className="text-center text-[#8B7D6B] mb-6">Como se sentem hoje?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {energyPlanning.energyLevels.map((level) => (
              <button
                key={level.id}
                onClick={() => setSelectedLevel(level.id)}
                className={`p-6 rounded-xl text-center transition-all duration-300 ${
                  selectedLevel === level.id
                    ? 'bg-white shadow-lg scale-105 border-2'
                    : 'bg-white/60 hover:bg-white/80 border-2 border-transparent'
                }`}
                style={{ 
                  borderColor: selectedLevel === level.id ? level.color : 'transparent'
                }}
              >
                <span className="text-3xl mb-2 block">{level.emoji}</span>
                <span className="font-medium text-[#2C2C2C] text-sm">{level.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Level Content */}
        {selectedLevel && (
          <div className="animate-fade-in">
            {energyPlanning.energyLevels
              .filter(level => level.id === selectedLevel)
              .map(level => (
                <div key={level.id}>
                  {/* Description */}
                  <div 
                    className="rounded-xl p-6 mb-8 text-center"
                    style={{ backgroundColor: `${level.color}20` }}
                  >
                    <p className="text-[#6B4E37] mb-2">{level.description}</p>
                    <p className="text-[#8B7D6B] text-sm italic">{level.message}</p>
                  </div>

                  {/* Tasks */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-4">
                      Tarefas sugeridas para este momento
                    </h3>
                    {level.tasks.map((task, index) => {
                      const taskId = `${level.id}-${index}`;
                      const isCompleted = completedTasks.includes(taskId);
                      
                      return (
                        <div 
                          key={index}
                          className={`bg-white/80 backdrop-blur-sm rounded-xl p-5 flex items-center gap-4 transition-all duration-300 ${
                            isCompleted ? 'opacity-60' : ''
                          }`}
                        >
                          <button
                            onClick={() => toggleTask(level.id, index)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                              isCompleted 
                                ? 'bg-[#B5C4B1] border-[#B5C4B1]' 
                                : 'border-[#D4C4B0] hover:border-[#C4B5A0]'
                            }`}
                          >
                            {isCompleted && <Check size={14} className="text-white" />}
                          </button>
                          
                          <div className="flex-1">
                            <p className={`text-[#2C2C2C] ${isCompleted ? 'line-through' : ''}`}>
                              {task.task}
                            </p>
                          </div>

                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1 text-[#8B7D6B]">
                              <Clock size={14} />
                              {task.duration}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              task.importance === 'alta' 
                                ? 'bg-[#E8D5D5]/50 text-[#6B4E37]'
                                : task.importance === 'média'
                                ? 'bg-[#D4C4B0]/50 text-[#6B4E37]'
                                : 'bg-[#B5C4B1]/30 text-[#6B4E37]'
                            }`}>
                              {task.importance}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Completed Counter */}
                  {completedTasks.filter(t => t.startsWith(level.id)).length > 0 && (
                    <div className="mt-6 text-center">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#B5C4B1]/20 rounded-full">
                        <Star size={16} className="text-[#B5C4B1]" />
                        <span className="text-sm text-[#6B4E37]">
                          {completedTasks.filter(t => t.startsWith(level.id)).length} de {level.tasks.length} concluídas
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {!selectedLevel && (
          <div className="text-center py-12 bg-white/40 rounded-xl">
            <p className="text-[#8B7D6B]">
              Escolham o vosso nível de energia atual para ver tarefas adequadas
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnergyTool;
